import { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import useClickOutside from '../../../hooks/useClickOutside';

interface DateRangeFilterProps {
  selectedRange: '1d' | '7d' | '30d' | '90d';
  onRangeChange: (range: '1d' | '7d' | '30d' | '90d') => void;
  startDate: Date;
  endDate: Date;
  isCustomRange: boolean;
  onCustomRangeApply: (start: Date, end: Date) => void;
}

type RangeKey = '1d' | '7d' | '30d' | '90d';

const presets: { key: RangeKey; label: string }[] = [
  { key: '1d', label: '1j' },
  { key: '7d', label: '7j' },
  { key: '30d', label: '30j' },
  { key: '90d', label: '90j' },
];

const RECENT_RANGES_KEY = 'station-overview-recent-ranges';
const MAX_RECENT = 3;

interface StoredRange {
  start: string;
  end: string;
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
};

const formatInputDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const loadRecentRanges = (): StoredRange[] => {
  try {
    const raw = localStorage.getItem(RECENT_RANGES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveRecentRange = (start: Date, end: Date) => {
  const entry: StoredRange = { start: formatInputDate(start), end: formatInputDate(end) };
  const existing = loadRecentRanges();
  const filtered = existing.filter(
    (r) => r.start !== entry.start || r.end !== entry.end,
  );
  filtered.unshift(entry);
  localStorage.setItem(RECENT_RANGES_KEY, JSON.stringify(filtered.slice(0, MAX_RECENT)));
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  selectedRange,
  onRangeChange,
  startDate,
  endDate,
  isCustomRange,
  onCustomRangeApply,
}) => {
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState(formatInputDate(startDate));
  const [customEnd, setCustomEnd] = useState(formatInputDate(endDate));
  const [activeSection, setActiveSection] = useState<RangeKey | 'custom'>(
    isCustomRange ? 'custom' : selectedRange,
  );
  const popoverRef = useRef<HTMLDivElement>(null);

  const closePopover = useCallback(() => setOpen(false), []);
  useClickOutside(popoverRef, closePopover, open);

  const handleToggle = () => {
    if (!open) {
      setCustomStart(formatInputDate(startDate));
      setCustomEnd(formatInputDate(endDate));
      setActiveSection(isCustomRange ? 'custom' : selectedRange);
    }
    setOpen(!open);
  };

  const handlePresetClick = (key: RangeKey) => {
    onRangeChange(key);
    setOpen(false);
  };

  const handleApply = () => {
    const start = new Date(customStart + 'T00:00:00');
    const end = new Date(customEnd + 'T23:59:59');
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return;
    saveRecentRange(start, end);
    setRecentRanges(loadRecentRanges());
    onCustomRangeApply(start, end);
    setOpen(false);
  };

  const handleReset = () => {
    setCustomStart(formatInputDate(startDate));
    setCustomEnd(formatInputDate(endDate));
  };

  const handleRecentClick = (range: StoredRange) => {
    const start = new Date(range.start + 'T00:00:00');
    const end = new Date(range.end + 'T23:59:59');
    saveRecentRange(start, end);
    setRecentRanges(loadRecentRanges());
    onCustomRangeApply(start, end);
    setOpen(false);
  };

  const [recentRanges, setRecentRanges] = useState<StoredRange[]>(() => loadRecentRanges());

  return (
    <div className="relative" ref={popoverRef}>
      <div className="flex items-center gap-1">
        {/* Date label button */}
        <button
          type="button"
          onClick={handleToggle}
          className={`pl-3 pr-8 py-1.5 rounded-md border border-neutral-10 flex items-center cursor-pointer transition-colors typo-body relative ${
            isCustomRange
              ? 'bg-neutral text-white'
              : 'bg-white text-neutral-60 hover:text-neutral hover:bg-neutral-5'
          }`}
        >
          <span>
            {formatDate(startDate)} - {formatDate(endDate)}
          </span>
          <ChevronDownIcon className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
            isCustomRange ? 'text-white' : 'text-neutral-40'
          }`} strokeWidth={3} />
        </button>

        {/* Quick range pills */}
        <div className="flex items-center gap-1">
          {presets.map((range) => (
            <button
              key={range.key}
              type="button"
              className={`px-2.5 py-1 rounded-md typo-body transition-colors ${
                !isCustomRange && selectedRange === range.key
                  ? 'bg-neutral text-white !font-semibold cursor-default'
                  : 'text-neutral-60 hover:text-neutral'
              }`}
              onClick={() => onRangeChange(range.key)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Popover */}
      {open && (
        <div
          className="absolute top-0 left-0 -mt-1 -ml-1 z-50 flex bg-white"
          style={{
            width: 520,
            borderRadius: 10,
            boxShadow: '0px 8px 30px rgba(0,0,0,0.14)',
            border: '1px solid rgba(26,26,26,0.08)',
          }}
        >
          {/* Left panel — Presets */}
          <div
            className="flex flex-col"
            style={{
              width: 140,
              padding: '8px 0',
              borderRight: '1px solid rgba(26,26,26,0.08)',
            }}
          >
            {presets.map((preset) => (
              <button
                key={preset.key}
                type="button"
                onClick={() => handlePresetClick(preset.key)}
                className="text-left transition-colors"
                style={{
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: activeSection === preset.key ? 700 : 500,
                  color:
                    activeSection === preset.key
                      ? '#FF8A4C'
                      : 'rgba(26,26,26,0.6)',
                  background:
                    activeSection === preset.key
                      ? 'rgba(255,138,76,0.06)'
                      : 'transparent',
                  borderLeft:
                    activeSection === preset.key
                      ? '2px solid #FF8A4C'
                      : '2px solid transparent',
                }}
              >
                {preset.label}
              </button>
            ))}

            {/* Separator */}
            <div
              className="mx-3 my-1"
              style={{ borderTop: '1px solid rgba(26,26,26,0.08)' }}
            />

            {/* Personnalisé */}
            <button
              type="button"
              onClick={() => setActiveSection('custom')}
              className="text-left flex items-center justify-between transition-colors"
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: activeSection === 'custom' ? 700 : 500,
                color:
                  activeSection === 'custom'
                    ? '#FF8A4C'
                    : 'rgba(26,26,26,0.6)',
                background:
                  activeSection === 'custom'
                    ? 'rgba(255,138,76,0.06)'
                    : 'transparent',
                borderLeft:
                  activeSection === 'custom'
                    ? '2px solid #FF8A4C'
                    : '2px solid transparent',
              }}
            >
              Personnalisé
              <span style={{ fontSize: 10 }}>▸</span>
            </button>
          </div>

          {/* Right panel — Custom */}
          <div className="flex-1 flex flex-col" style={{ padding: 16 }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'rgba(26,26,26,0.4)',
                }}
              >
                Personnalisé
              </span>
              <button
                type="button"
                onClick={handleReset}
                style={{ fontSize: 12, fontWeight: 500, color: '#FF8A4C' }}
                className="hover:opacity-80 transition-opacity"
              >
                Réinitialiser
              </button>
            </div>

            {/* Date inputs */}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="flex-1"
                style={{
                  height: 36,
                  borderRadius: 6,
                  border: '1px solid rgba(26,26,26,0.15)',
                  padding: '0 10px',
                  fontSize: 13,
                  color: 'rgba(26,26,26,0.8)',
                  outline: 'none',
                }}
              />
              <span style={{ color: 'rgba(26,26,26,0.3)', fontSize: 14 }}>→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="flex-1"
                style={{
                  height: 36,
                  borderRadius: 6,
                  border: '1px solid rgba(26,26,26,0.15)',
                  padding: '0 10px',
                  fontSize: 13,
                  color: 'rgba(26,26,26,0.8)',
                  outline: 'none',
                }}
              />
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-2 mb-4">
              <button
                type="button"
                onClick={closePopover}
                style={{ fontSize: 13, fontWeight: 500, color: 'rgba(26,26,26,0.5)' }}
                className="px-3 py-1 hover:opacity-80 transition-opacity"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="hover:opacity-90 transition-opacity"
                style={{
                  background: '#FF8A4C',
                  borderRadius: 6,
                  height: 32,
                  padding: '0 14px',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Appliquer
              </button>
            </div>

            {/* Recent section */}
            {recentRanges.length > 0 && (
              <div
                style={{
                  borderTop: '1px solid rgba(26,26,26,0.08)',
                  paddingTop: 12,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'rgba(26,26,26,0.4)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Récent
                </span>
                <div className="flex flex-col gap-1 mt-2">
                  {recentRanges.map((range) => {
                    const s = new Date(range.start + 'T00:00:00');
                    const e = new Date(range.end + 'T00:00:00');
                    return (
                      <button
                        key={`${range.start}-${range.end}`}
                        type="button"
                        onClick={() => handleRecentClick(range)}
                        className="text-left hover:opacity-70 transition-opacity"
                        style={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: 'rgba(26,26,26,0.6)',
                          padding: '4px 0',
                        }}
                      >
                        {formatDate(s)} - {formatDate(e)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
